import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  Zap, ArrowLeft, Save, User, MapPin, Plus, X,
  Instagram, Youtube, AlertCircle, CheckCircle, Camera, Loader2,
  CreditCard, ExternalLink, Check
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const CreatorProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['dashboard', 'common']);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [settingUpPayout, setSettingUpPayout] = useState(false);
  const fileInputRef = useRef(null);
  
  const [niches, setNiches] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [audienceTypes, setAudienceTypes] = useState([]);
  
  const [profile, setProfile] = useState({
    bio: "",
    niches: [],
    social_platforms: [],
    audience_type: "",
    location: "",
    avatar_url: ""
  });
  
  const [newPlatform, setNewPlatform] = useState({
    platform: "",
    handle: "",
    followers: "",
    profile_url: ""
  });

  // All API calls use httpOnly cookies (withCredentials: true)
  const axiosConfig = { withCredentials: true };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, nichesRes, platformsRes, audienceRes, connectRes] = await Promise.all([
          axios.get(`${API}/creator/profile`, axiosConfig),
          axios.get(`${API}/niches`),
          axios.get(`${API}/platforms`),
          axios.get(`${API}/audience-types`),
          axios.get(`${API}/creator/connect/status`, axiosConfig).catch(() => ({ data: { has_account: false } }))
        ]);
        
        // Safely handle profile data with defaults
        const profileData = profileRes.data || {};
        
        // Normalize social_platforms - handle both old format (strings) and new format (objects)
        let normalizedPlatforms = [];
        if (Array.isArray(profileData.social_platforms)) {
          normalizedPlatforms = profileData.social_platforms.map(sp => {
            // If it's already an object with platform property, use it
            if (sp && typeof sp === 'object' && sp.platform) {
              return sp;
            }
            // If it's a string (old format), convert to object
            if (typeof sp === 'string') {
              return { platform: sp, handle: "", followers: "", profile_url: "" };
            }
            return null;
          }).filter(Boolean);
        }
        
        // Normalize audience_type - handle both array (old) and string (new) formats
        let normalizedAudienceType = "";
        if (Array.isArray(profileData.audience_type)) {
          normalizedAudienceType = profileData.audience_type[0] || "";
        } else {
          normalizedAudienceType = profileData.audience_type || "";
        }
        
        setProfile({
          bio: profileData.bio || "",
          niches: Array.isArray(profileData.niches) ? profileData.niches : [],
          social_platforms: normalizedPlatforms,
          audience_type: normalizedAudienceType,
          location: profileData.location || "",
          avatar_url: profileData.avatar_url || ""
        });
        
        // Safely handle arrays with defaults
        setNiches(Array.isArray(nichesRes.data?.niches) ? nichesRes.data.niches : []);
        setPlatforms(Array.isArray(platformsRes.data?.platforms) ? platformsRes.data.platforms : []);
        setAudienceTypes(Array.isArray(audienceRes.data?.audience_types) ? audienceRes.data.audience_types : []);
        
        // Set connect status
        setConnectStatus(connectRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        // Set empty defaults on error
        setNiches([]);
        setPlatforms([]);
        setAudienceTypes([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    
    // Handle Stripe Connect return
    const connectParam = searchParams.get("connect");
    if (connectParam === "complete") {
      setNotification({ type: "success", message: t('dashboard:creator.profile.payoutConnected', 'Payout account connected successfully!') });
      // Refresh connect status
      axios.get(`${API}/creator/connect/status`, axiosConfig)
        .then(res => setConnectStatus(res.data))
        .catch(() => {});
    } else if (connectParam === "refresh") {
      setNotification({ type: "warning", message: t('dashboard:creator.profile.payoutIncomplete', 'Please complete your payout account setup.') });
    }
  }, [searchParams, t]);

  const toggleNiche = (niche) => {
    setProfile(prev => ({
      ...prev,
      niches: Array.isArray(prev.niches) && prev.niches.includes(niche)
        ? prev.niches.filter(n => n !== niche)
        : [...(prev.niches || []), niche]
    }));
  };

  const addSocialPlatform = () => {
    if (!newPlatform.platform || (!newPlatform.handle && !newPlatform.profile_url)) return;
    
    setProfile(prev => ({
      ...prev,
      social_platforms: [...(prev.social_platforms || []), { ...newPlatform }]
    }));
    setNewPlatform({ platform: "", handle: "", followers: "", profile_url: "" });
  };

  const removeSocialPlatform = (index) => {
    setProfile(prev => ({
      ...prev,
      social_platforms: (prev.social_platforms || []).filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/creator/profile`, profile, axiosConfig);
      setNotification({ type: "success", message: t('common:messages.saved') });
    } catch (error) {
      setNotification({ type: "error", message: t('common:errors.somethingWentWrong') });
    } finally {
      setSaving(false);
    }
  };

  // Handle avatar image upload
  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setNotification({ 
        type: "error", 
        message: t('dashboard:creator.profile.invalidImageType', 'Please upload a valid image (JPG, PNG, WebP, or GIF)')
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setNotification({ 
        type: "error", 
        message: t('dashboard:creator.profile.imageTooLarge', 'Image must be less than 5MB')
      });
      return;
    }

    setUploadingAvatar(true);
    try {
      // Get Cloudinary signature
      const sigRes = await axios.get(
        `${API}/cloudinary/signature?resource_type=image&folder=profiles/${user?.user_id}`,
        axiosConfig
      );
      const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', api_key);
      formData.append('timestamp', timestamp);
      formData.append('signature', signature);
      formData.append('folder', folder);

      const uploadRes = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        formData
      );

      const avatarUrl = uploadRes.data.secure_url;

      // Save to backend
      await axios.put(`${API}/creator/profile/avatar`, { avatar_url: avatarUrl }, axiosConfig);

      // Update local state
      setProfile(prev => ({ ...prev, avatar_url: avatarUrl }));
      setNotification({ type: "success", message: t('dashboard:creator.profile.avatarUpdated', 'Profile photo updated!') });
    } catch (error) {
      console.error("Avatar upload error:", error);
      setNotification({ 
        type: "error", 
        message: t('dashboard:creator.profile.avatarUploadFailed', 'Failed to upload photo. Please try again.')
      });
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle Stripe Connect payout setup
  const handleSetupPayout = async () => {
    setSettingUpPayout(true);
    setNotification(null); // Clear any previous notifications
    
    try {
      if (!connectStatus?.has_account) {
        // Create new Connect account
        const response = await axios.post(
          `${API}/creator/connect/create-account`,
          { origin_url: window.location.origin, country: "DK" },
          axiosConfig
        );
        
        if (response.data?.onboarding_url) {
          // Redirect to Stripe onboarding
          window.location.href = response.data.onboarding_url;
        } else {
          throw new Error("No onboarding URL received");
        }
      } else if (!connectStatus?.payouts_enabled) {
        // Account exists but not fully onboarded - get new onboarding link
        const response = await axios.post(
          `${API}/creator/connect/onboarding-link`,
          { origin_url: window.location.origin },
          axiosConfig
        );
        
        if (response.data?.onboarding_url) {
          window.location.href = response.data.onboarding_url;
        } else {
          throw new Error("No onboarding URL received");
        }
      }
    } catch (error) {
      console.error("Payout setup error:", error);
      
      // Parse error message for better user feedback
      let errorMessage = t('dashboard:creator.profile.payoutSetupFailed', 'Failed to setup payout. Please try again.');
      const detail = error.response?.data?.detail || error.message;
      
      if (detail?.includes("signed up for Connect")) {
        errorMessage = t('dashboard:creator.profile.stripeConnectNotEnabled', 'Stripe Connect is not enabled. Please contact support.');
      } else if (detail) {
        errorMessage = detail;
      }
      
      setNotification({ type: "error", message: errorMessage });
      setSettingUpPayout(false);
    }
  };

  // Safe platform icon getter
  const getPlatformIcon = (platformName) => {
    if (!platformName) return <span className="text-purple-400 font-bold">?</span>;
    
    if (platformName === "Instagram") {
      return <Instagram className="w-5 h-5 text-purple-400" />;
    } else if (platformName === "YouTube") {
      return <Youtube className="w-5 h-5 text-red-400" />;
    } else {
      return <span className="text-purple-400 font-bold">{platformName.charAt(0)}</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05060D] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05060D]">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/creator")}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('common:actions.back')}
          </button>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
                alt="EleKtro"
                className="w-8 h-8"
              />
              <span className="font-bold text-white">EleKtro Universal</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Notification */}
        {notification && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            notification.type === "success" 
              ? "bg-green-500/10 border border-green-500/30 text-green-400" 
              : "bg-red-500/10 border border-red-500/30 text-red-400"
          }`}>
            {notification.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-auto hover:opacity-70">×</button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold font-['Unbounded'] text-white mb-2">
              {t('dashboard:creator.profile.title')}
            </h1>
            <p className="text-gray-400">
              {t('dashboard:creator.profile.subtitle')}
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-profile-btn"
            className="btn-primary px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <Save className="w-5 h-5" />
            )}
            {saving ? t('common:actions.saving') : t('common:actions.save')}
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-xl font-bold font-['Syne'] text-white mb-6">{t('dashboard:creator.profile.basicInfo')}</h2>
            
            {/* Profile Photo Upload */}
            <div className="flex flex-col sm:flex-row items-center gap-6 mb-6 pb-6 border-b border-white/10">
              <div className="relative group">
                <div 
                  className={`w-24 h-24 rounded-full overflow-hidden border-2 ${
                    profile.avatar_url ? 'border-cyan-500/50' : 'border-dashed border-white/20'
                  } bg-white/5 flex items-center justify-center`}
                >
                  {uploadingAvatar ? (
                    <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                  ) : profile.avatar_url ? (
                    <img 
                      src={profile.avatar_url} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Camera className="w-8 h-8 text-gray-500" />
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  id="avatar-upload"
                  data-testid="avatar-upload-input"
                />
                <label
                  htmlFor="avatar-upload"
                  className="absolute inset-0 rounded-full cursor-pointer bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <Camera className="w-6 h-6 text-white" />
                </label>
              </div>
              <div className="text-center sm:text-left">
                <label
                  htmlFor="avatar-upload"
                  className="btn-primary px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer inline-flex items-center gap-2"
                  data-testid="upload-photo-btn"
                >
                  <Camera className="w-4 h-4" />
                  {profile.avatar_url 
                    ? t('dashboard:creator.profile.changePhoto', 'Change Photo')
                    : t('dashboard:creator.profile.uploadPhoto', 'Upload Photo')
                  }
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  {t('dashboard:creator.profile.photoHint', 'JPG, PNG, WebP or GIF. Max 5MB.')}
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  {t('dashboard:creator.profile.displayName')}
                </label>
                <input
                  type="text"
                  value={user?.name || ""}
                  disabled
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-gray-400"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  {t('dashboard:creator.profile.location')}
                </label>
                <input
                  type="text"
                  value={profile.location || ""}
                  onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  data-testid="profile-location"
                  placeholder={t('dashboard:creator.profile.locationPlaceholder')}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('dashboard:creator.profile.bio')}</label>
              <textarea
                value={profile.bio || ""}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                data-testid="profile-bio"
                placeholder={t('dashboard:creator.profile.bioPlaceholder')}
                rows={4}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400 resize-none"
              />
            </div>
          </div>

          {/* Niches */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-xl font-bold font-['Syne'] text-white mb-2">{t('dashboard:creator.profile.niches')}</h2>
            <p className="text-gray-400 mb-6">{t('dashboard:creator.profile.nichesSubtitle')}</p>
            
            {niches.length === 0 ? (
              <p className="text-gray-500 text-sm">{t('common:forms.noResults')}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {niches.map((niche) => (
                  <button
                    key={niche}
                    onClick={() => toggleNiche(niche)}
                    data-testid={`niche-${niche}`}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      Array.isArray(profile.niches) && profile.niches.includes(niche)
                        ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/50"
                        : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/30"
                    }`}
                  >
                    {niche}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Audience */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-xl font-bold font-['Syne'] text-white mb-2">{t('dashboard:creator.profile.audience')}</h2>
            <p className="text-gray-400 mb-6">{t('dashboard:creator.profile.audienceSubtitle')}</p>
            
            {audienceTypes.length === 0 ? (
              <p className="text-gray-500 text-sm">{t('common:forms.noResults')}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {audienceTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setProfile({ ...profile, audience_type: type })}
                    data-testid={`audience-${type}`}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      profile.audience_type === type
                        ? "bg-blue-500/20 text-blue-400 border border-blue-500/50"
                        : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/30"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Social Platforms */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-xl font-bold font-['Syne'] text-white mb-2">{t('dashboard:creator.profile.platforms')}</h2>
            <p className="text-gray-400 mb-6">{t('dashboard:creator.profile.platformsSubtitle')}</p>
            
            {/* Existing Platforms */}
            {Array.isArray(profile.social_platforms) && profile.social_platforms.length > 0 && (
              <div className="space-y-3 mb-6">
                {profile.social_platforms
                  .filter(sp => sp && (sp.platform || sp.handle || sp.profile_url))
                  .map((sp, index) => (
                  <div key={`${sp?.platform || 'platform'}-${index}`} className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      {getPlatformIcon(sp?.platform)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-white">{sp?.platform || t('dashboard:creator.profile.unknownPlatform')}</div>
                      <div className="text-sm text-gray-400">
                        {sp?.handle && <span>{sp.handle}</span>}
                        {sp?.followers && <span> • {sp.followers} {t('dashboard:creator.profile.followers')}</span>}
                      </div>
                      {sp?.profile_url && (
                        <a 
                          href={sp.profile_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-cyan-400 hover:text-cyan-300 truncate block mt-1"
                        >
                          {sp.profile_url}
                        </a>
                      )}
                    </div>
                    <button
                      onClick={() => removeSocialPlatform(index)}
                      className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-red-400 flex-shrink-0"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state - only show if no valid platforms exist */}
            {(!Array.isArray(profile.social_platforms) || 
              profile.social_platforms.filter(sp => sp && (sp.platform || sp.handle || sp.profile_url)).length === 0) && (
              <div className="text-center py-6 mb-6 bg-white/5 rounded-xl border border-dashed border-white/10">
                <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-3">
                  <Instagram className="w-6 h-6 text-purple-400" />
                </div>
                <p className="text-gray-400 text-sm">{t('dashboard:creator.profile.noPlatformsYet')}</p>
                <p className="text-gray-500 text-xs mt-1">{t('dashboard:creator.profile.addFirstPlatform')}</p>
              </div>
            )}

            {/* Add New Platform */}
            <div className="p-4 bg-white/5 rounded-xl border border-dashed border-white/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <select
                  value={newPlatform.platform}
                  onChange={(e) => setNewPlatform({ ...newPlatform, platform: e.target.value })}
                  data-testid="new-platform-select"
                  className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-cyan-400"
                >
                  <option value="">{t('dashboard:creator.profile.selectPlatform')}</option>
                  {platforms.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={newPlatform.handle}
                  onChange={(e) => setNewPlatform({ ...newPlatform, handle: e.target.value })}
                  data-testid="new-platform-handle"
                  placeholder={t('dashboard:creator.profile.handlePlaceholder')}
                  className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="url"
                  value={newPlatform.profile_url}
                  onChange={(e) => setNewPlatform({ ...newPlatform, profile_url: e.target.value })}
                  data-testid="new-platform-url"
                  placeholder={t('dashboard:creator.profile.profileUrlPlaceholder')}
                  className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400"
                />
                <input
                  type="text"
                  value={newPlatform.followers}
                  onChange={(e) => setNewPlatform({ ...newPlatform, followers: e.target.value })}
                  data-testid="new-platform-followers"
                  placeholder={t('dashboard:creator.profile.followersPlaceholder')}
                  className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-400"
                />
              </div>
              <p className="text-xs text-gray-500 mb-4">{t('dashboard:creator.profile.platformHelp')}</p>
              <button
                onClick={addSocialPlatform}
                disabled={!newPlatform.platform || (!newPlatform.handle && !newPlatform.profile_url)}
                data-testid="add-platform-btn"
                className="w-full py-3 rounded-xl border border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Plus className="w-5 h-5" />
                {t('dashboard:creator.profile.addPlatform')}
              </button>
            </div>
          </div>

          {/* Payout Information */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-xl font-bold font-['Syne'] text-white mb-6">
              {t('dashboard:creator.profile.payoutInfo', 'Payout Information')}
            </h2>
            
            {connectStatus?.payouts_enabled ? (
              // Connected State
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-green-400 font-semibold">
                      {t('dashboard:creator.profile.payoutConnectedStatus', 'Payout Account Connected')}
                    </div>
                    <div className="text-sm text-gray-400">
                      {t('dashboard:creator.profile.payoutReadyDesc', 'Your Stripe account is verified and ready to receive payouts.')}
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-white/5 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-400 mb-1">{t('dashboard:creator.profile.payoutMethod', 'Payout Method')}</div>
                      <div className="text-white font-medium flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-cyan-400" />
                        Stripe Connect
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-400 mb-1">{t('dashboard:creator.profile.payoutStatus', 'Status')}</div>
                      <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm font-medium">
                        {t('dashboard:creator.profile.active', 'Active')}
                      </span>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleSetupPayout}
                  className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('dashboard:creator.profile.managePayoutAccount', 'Manage payout account in Stripe')}
                </button>
              </div>
            ) : connectStatus?.has_account ? (
              // Pending State - account created but not fully verified
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-yellow-400 font-semibold">
                      {t('dashboard:creator.profile.payoutPendingStatus', 'Setup Incomplete')}
                    </div>
                    <div className="text-sm text-gray-400">
                      {t('dashboard:creator.profile.payoutPendingDesc', 'Please complete your Stripe account verification to receive payouts.')}
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleSetupPayout}
                  disabled={settingUpPayout}
                  data-testid="complete-payout-btn"
                  className="w-full btn-primary py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2"
                >
                  {settingUpPayout ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <ExternalLink className="w-5 h-5" />
                      {t('dashboard:creator.profile.completePayoutSetup', 'Complete Payout Setup')}
                    </>
                  )}
                </button>
              </div>
            ) : (
              // Not Connected State
              <div className="space-y-4">
                <div className="text-center py-6 bg-white/5 rounded-xl border border-dashed border-white/10">
                  <div className="w-16 h-16 rounded-full bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
                    <CreditCard className="w-8 h-8 text-cyan-400" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">
                    {t('dashboard:creator.profile.noPayoutSetup', 'No Payout Account')}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 max-w-md mx-auto">
                    {t('dashboard:creator.profile.payoutSetupDesc', 'Connect your Stripe account to receive earnings from your campaigns. Fast, secure payouts directly to your bank.')}
                  </p>
                  <div className="flex flex-wrap justify-center gap-3 text-xs text-gray-500 mb-6">
                    <span className="flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-400" />
                      {t('dashboard:creator.profile.payoutFeature1', 'Instant transfers')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-400" />
                      {t('dashboard:creator.profile.payoutFeature2', 'Bank-level security')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-400" />
                      {t('dashboard:creator.profile.payoutFeature3', 'No hidden fees')}
                    </span>
                  </div>
                </div>
                
                <button
                  onClick={handleSetupPayout}
                  disabled={settingUpPayout}
                  data-testid="setup-payout-btn"
                  className="w-full btn-primary py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2"
                >
                  {settingUpPayout ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      {t('dashboard:creator.profile.setupPayout', 'Set Up Payout Account')}
                    </>
                  )}
                </button>
                
                <p className="text-xs text-gray-500 text-center">
                  {t('dashboard:creator.profile.stripeSecure', 'Powered by Stripe. Your financial data is encrypted and secure.')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorProfile;
